import { useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { MODAL_HANDLE_HEIGHT } from '@/feature/alerts/components/Modal';
import { PLAYER_ROW_HEIGHT } from '@/feature/lobby/components/PlayerRow';
import { isWeb } from '@/utils/constants-platform-specific';

import {
    CARD_BORDER_WIDTH,
    CARD_BOTTOM_PADDING,
    LIST_PADDING_TOP,
    LIST_SEPARATOR_HEIGHT,
    QUICK_AMOUNTS_HEIGHT,
    RACE_TO_PLACE_ACTION_BUTTON_HEIGHT,
    RACE_TO_PLACE_BOTTOM_SEPARATOR_HEIGHT,
    RACE_TO_PLACE_FILL_METER_HEIGHT,
    RACE_TO_PLACE_MODAL_MARGIN_TOP,
    RACE_TO_PLACE_MODAL_SHEET_TITLE_HEIGHT,
    RaceToPlaceAmountSelectorType,
    SHEET_BOTTOM_PADDING,
    spacing,
} from '../race-to-place-constants';
import { RaceToPlaceAmountState } from './use-race-to-place-amount-state';

/**
 * Calculate the total amount section height
 */
const getAmountSectionTotalHeight = (
    { visibility, heights }: RaceToPlaceAmountState,
    selector: RaceToPlaceAmountSelectorType,
    buttonHeight: number
): number => {
    const isNumberPad = selector === 'number-pad';
    const { showToWin, showSelectorToButtonGap } = visibility;
    const { selectorHeight, errorOrSpacerHeight, toWinLabelHeight, keyboardTotalHeight, selectorToButtonGap } = heights;

    return (
        selectorHeight +
        errorOrSpacerHeight +
        (showToWin ? toWinLabelHeight : 0) +
        (isNumberPad ? keyboardTotalHeight : 0) +
        (showSelectorToButtonGap ? selectorToButtonGap : 0) +
        buttonHeight
    );
};

/**
 * Calculate content height below the list (fillMeter + amountSection + cardBottomPadding).
 */
const getContentBelowListHeight = (
    amountState: RaceToPlaceAmountState,
    selector: RaceToPlaceAmountSelectorType
): number => {
    const amountSectionHeight = getAmountSectionTotalHeight(amountState, selector, RACE_TO_PLACE_ACTION_BUTTON_HEIGHT);
    return RACE_TO_PLACE_FILL_METER_HEIGHT + amountSectionHeight + CARD_BOTTOM_PADDING;
};

/**
 * Calculate the player list container height (includes FlatList + bottom separator).
 */
const getPlayerListContainerHeight = (numPicks: number): number => {
    const rowHeight = PLAYER_ROW_HEIGHT + (isWeb ? 8 : 0);
    const playerRowsHeight = numPicks * rowHeight + (numPicks - 1) * LIST_SEPARATOR_HEIGHT;
    return LIST_PADDING_TOP + playerRowsHeight + RACE_TO_PLACE_BOTTOM_SEPARATOR_HEIGHT;
};

export const RACE_TO_PLACE_MODAL_BASE_HEIGHT =
    getPlayerListContainerHeight(2) +
    RACE_TO_PLACE_FILL_METER_HEIGHT +
    QUICK_AMOUNTS_HEIGHT +
    spacing.SELECTOR_TO_BUTTON +
    RACE_TO_PLACE_ACTION_BUTTON_HEIGHT +
    CARD_BOTTOM_PADDING;

type RaceToPlaceHeightsParams = {
    amountState: RaceToPlaceAmountState;
    selector: RaceToPlaceAmountSelectorType;
    numPicks: number;
};

type RaceToPlaceHeights = {
    cardHeight: number;
    listMaxHeight: number;
    isScrollable: boolean;
};

/**
 * Returns both the card height and the max list container height.
 *
 * Layout from top to bottom:
 * - top inset
 * - modal handle (24)
 * - title row (24)
 * - stacked cards visible offset (12)
 * - [CARD START]
 * -   list container (FlatList + bottom separator) <- listMaxHeight applies here
 * -   fill meter (36)
 * -   amount section (variable)
 * -   card bottom padding (16)
 * - [CARD END]
 * - sheet bottom padding (16)
 * - bottom inset
 */
export const useRaceToPlaceHeights = ({
    amountState,
    selector,
    numPicks,
}: RaceToPlaceHeightsParams): RaceToPlaceHeights => {
    const { top, bottom } = useSafeAreaInsets();
    const { height: screenHeight } = useWindowDimensions();

    const contentBelowListHeight = getContentBelowListHeight(amountState, selector);
    const playersListContainerHeight = getPlayerListContainerHeight(numPicks);
    // Card height includes border (1px top + 1px bottom)
    // This represents the height the card would need to be to fit all content without scrolling
    const fullCardHeight = playersListContainerHeight + contentBelowListHeight + CARD_BORDER_WIDTH * 2;
    const webModalPadding = isWeb ? 64 : 0;

    // Calculate max available space for the card
    const maxCardHeight =
        screenHeight -
        top -
        MODAL_HANDLE_HEIGHT -
        RACE_TO_PLACE_MODAL_SHEET_TITLE_HEIGHT -
        RACE_TO_PLACE_MODAL_MARGIN_TOP -
        SHEET_BOTTOM_PADDING -
        bottom -
        webModalPadding;

    // Clamp card height to available space
    const cardHeight = Math.min(fullCardHeight, maxCardHeight);

    // List container gets whatever space is left after content below it
    // Subtract border (top + bottom) since it reduces internal content space
    const listMaxHeight = cardHeight - contentBelowListHeight - CARD_BORDER_WIDTH * 2;

    // Only enable scrolling when content is clamped (doesn't fit)
    const isScrollable = cardHeight < fullCardHeight;

    return { cardHeight, listMaxHeight, isScrollable };
};
